import { getServerSession } from 'next-auth';

import { getKeycloakIssuer, keycloakProvider } from './providers';
import { jwtHandler } from './utils';

import type { GetServerSidePropsContext, NextApiRequest, NextApiResponse } from 'next';
import type { NextAuthOptions } from 'next-auth';
import { isSessionValid } from './auth';
import { redirect } from 'next/navigation';

import { logger } from '@lichtblick/logger';

export const configQmApp = {
  providers: [keycloakProvider],
  session: {
    strategy: 'jwt',
    // Set token expiration to 30 minutes
    maxAge: 24 * 60 * 60,
  },
  pages: {
    signIn: '/',
    signOut: '/',
  },
  callbacks: {
    signIn: async ({ account }) => {
      const issuer = getKeycloakIssuer();
      logger.info('NextAuth sign-in callback invoked', {
        component: 'AuthService',
        operation: 'signInQmApp',
        provider: account?.provider ?? 'unknown',
        hasIssuer: Boolean(issuer),
        issuerPreview: issuer.slice(0, 80),
      });

      return true;
    },
    jwt: jwtHandler,
    session: async ({ session, token }) => {
      if (token?.error) {
        const appUrl = process.env.NEXT_PUBLIC_APPLICATION_URL ?? '';
        if (appUrl) {
          const signoutUrl = new URL('/api/auth/signout', appUrl);
          logger.info('Session callback triggering app signout endpoint', {
            component: 'AuthService',
            operation: 'sessionSignOutQmApp',
            signoutHost: signoutUrl.host,
            signoutPath: signoutUrl.pathname,
          });
          await fetch(signoutUrl.toString(), { method: 'POST' });
        } else {
          logger.warn('Skipping app signout callback because NEXT_PUBLIC_APPLICATION_URL is missing', {
            component: 'AuthService',
            operation: 'sessionSignOutQmApp',
          });
        }
      }
      if (session && token && typeof token.access_token === 'string') {
        return {
          ...session,
          access_token: token.access_token ?? '',
          error: token.error,
        };
      }

      return { ...session, access_token: '' };
    },
  },
  events: {
    signOut: async ({ token: { id_token } }) => {
      const issuer = getKeycloakIssuer();
      if (!issuer) {
        logger.warn('Skipping Keycloak logout call because issuer is not configured', {
          component: 'AuthService',
          operation: 'signOutQmApp',
        });
        return;
      }

      const logoutUrl = new URL(`${issuer}/protocol/openid-connect/logout`);
      logoutUrl.searchParams.set('id_token_hint', String(id_token ?? ''));
      logger.info('Calling Keycloak logout endpoint', {
        component: 'AuthService',
        operation: 'signOutQmApp',
        logoutUrlHost: logoutUrl.host,
        logoutUrlPath: logoutUrl.pathname,
      });
      await fetch(logoutUrl.toString());
    },
  },
} satisfies NextAuthOptions;

// Use it in server contexts
export const authQmApp = async (
  ...args: [GetServerSidePropsContext['req'], GetServerSidePropsContext['res']] | [NextApiRequest, NextApiResponse] | []
) => {
  const session = await getServerSession(...args, configQmApp);

  return session;
};
